import { useCallback, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { PathologistComment } from "../services/workflows.service";
import { amendmentDraftQueryKey, useAmendmentDraft } from "./useAmendment";
import {
  amendmentService,
  type AmendmentDraft,
} from "../services/amendment.service";

export interface AmendmentDiagnosticData {
  schemaVersion: 1;
  diagnoses: Array<Record<string, any> & { specimenId: string }>;
  grossing: Array<Record<string, any> & { specimenId: string }>;
  caseLevelData: Record<string, any>;
  pathologistComments: PathologistComment[];
}
export interface AmendmentCommentContext {
  comments: any[];
  addComment: (commentText: string) => Promise<unknown>;
  deleteComment: (id: string) => Promise<unknown>;
}
export type AmendmentDiagnosticPatch = Partial<
  Omit<AmendmentDiagnosticData, "schemaVersion" | "pathologistComments">
> & {
  pathologistComments?: Array<
    Partial<PathologistComment> & { commentText: string }
  >;
};

export function useAmendmentDiagnosticDraft(caseId: string, enabled: boolean) {
  const queryClient = useQueryClient();
  const query = useAmendmentDraft(caseId, enabled);
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const savePatch = useCallback(
    (
      draftId: string,
      diagnosticPatch:
        | AmendmentDiagnosticPatch
        | ((data: AmendmentDiagnosticData) => AmendmentDiagnosticPatch)
    ) => {
      const operation = queue.current
        .catch(() => undefined)
        .then(async () => {
          await queryClient.cancelQueries({
            queryKey: amendmentDraftQueryKey(caseId),
          });
          const draft = queryClient.getQueryData<AmendmentDraft | null>(
            amendmentDraftQueryKey(caseId)
          );
          if (!draft?.diagnosticData || draft.id !== draftId)
            throw new Error("This amendment draft is no longer open.");
          const patch =
            typeof diagnosticPatch === "function"
              ? diagnosticPatch(draft.diagnosticData)
              : diagnosticPatch;
          const saved = await amendmentService.updateDraft(caseId, draftId, {
            diagnosticContext: true,
            diagnosticPatch: patch,
            revision: draft.revision,
          });
          const current = queryClient.getQueryData<AmendmentDraft | null>(
            amendmentDraftQueryKey(caseId)
          );
          if (current?.id === draftId)
            queryClient.setQueryData(amendmentDraftQueryKey(caseId), saved);
          return { success: true, savedCount: patch.diagnoses?.length ?? 0 };
        });
      queue.current = operation;
      return operation;
    },
    [caseId, queryClient]
  );
  const create = useCallback(async () => {
    const draft = await amendmentService.createDraft(caseId, {
      diagnosticContext: true,
    });
    queryClient.setQueryData(amendmentDraftQueryKey(caseId), draft);
    return draft;
  }, [caseId, queryClient]);
  const draft = useMemo(
    () => (query.data?.diagnosticData ? query.data : undefined),
    [query.data]
  );
  return { draft, query, create, savePatch };
}

export function overlayAmendmentCase(
  caseData: any,
  draft?: AmendmentDraft
): any {
  if (!caseData || !draft?.diagnosticData) return caseData;
  const data = draft.diagnosticData;
  return {
    ...caseData,
    ...data.caseLevelData,
    pathologistComments: data.pathologistComments,
    specimens: caseData.specimens.map((specimen: any) => {
      const diagnosis = data.diagnoses.find(
        (row) => row.specimenId === specimen.id
      );
      const gross = data.grossing.find((row) => row.specimenId === specimen.id);
      return {
        ...specimen,
        ...gross,
        gross: gross?.grossDescription ?? specimen.gross,
        internalGrossCodeId: gross?.grossCodeId ?? null,
        diagnosis: {
          ...specimen.diagnosis,
          ...diagnosis,
          cptDisplayCode:
            diagnosis?.cptDisplayCode ?? diagnosis?.cptCode ?? null,
          comments: "{}",
        },
        source: overlayGrossRelation(gross, "source", specimen.source),
        subsite: overlayGrossRelation(gross, "subsite", specimen.subsite),
      };
    }),
  };
}

function overlayGrossRelation(
  gross: Record<string, any> | undefined,
  key: "source" | "subsite",
  baseline: any
): any {
  if (gross?.[key] !== undefined) return gross[key];
  const id = gross?.[`${key}Id`];
  if (id === null) return null;
  if (id === undefined) return baseline;
  const name = gross?.[`${key}Name`];
  return {
    ...(baseline?.id === id ? baseline : {}),
    id,
    ...(name !== undefined ? { name } : {}),
  };
}
